import React, { useEffect, useRef, memo } from 'react';
import { View, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import ReAnimated, {
    useAnimatedStyle,
    useSharedValue,
    withRepeat,
    withTiming,
    Easing,
    interpolate,
    cancelAnimation
} from 'react-native-reanimated';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';

const ContentLoader = memo(({ 
    containerHeight = 85, 
    containerWidth = "100%", 
    borderRadius = 5, 
    spacingVertical = 8, 
    staggerPosition = 0, 
    style = {}, 
    invert = false 
}) => {
    const { theme: { colors } } = useUnistyles();
    const shimmerProgress = useSharedValue(0);
    const containerWidthValue = useSharedValue(0);
    const hasStartedRef = useRef(false);
    const delayTimerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (delayTimerRef.current) clearTimeout(delayTimerRef.current);
            cancelAnimation(shimmerProgress);
        };
    }, [shimmerProgress]);

    const animatedStyle = useAnimatedStyle(() => {
        if (containerWidthValue.value === 0) {
            return { opacity: 0 };
        }
        const translateX = interpolate(
            shimmerProgress.value, 
            [0, 1], 
            [-containerWidthValue.value, containerWidthValue.value * 2]
        );
        return {
            transform: [{ translateX }],
            opacity: 1,
        };
    });

    const startAnimation = () => {
        const delay = staggerPosition * 80;
        delayTimerRef.current = setTimeout(() => {
            shimmerProgress.value = withRepeat(
                withTiming(1, { 
                    duration: 1400, 
                    easing: Easing.bezier(0.4, 0, 0.2, 1) 
                }),
                -1,
                false
            );
        }, delay);
    };

    const handleLayout = (e) => {
        const width = e.nativeEvent.layout.width;
        containerWidthValue.value = width;
        if (!hasStartedRef.current) {
            hasStartedRef.current = true;
            startAnimation();
        }
    };

    const baseColor = invert ? colors.background : colors.surface;
    const shimmerColor = invert ? colors.surface : colors.border;

    return (
        <View 
            style={[styles.container(borderRadius, containerWidth, containerHeight, spacingVertical, baseColor), style]}
            onLayout={handleLayout}
        >
            <ReAnimated.View style={[styles.shimmerWrapper, animatedStyle]}>
                {Platform.OS === 'web' ? (
                    <View style={{
                        width: '100%',
                        height: '100%',
                        background: `linear-gradient(to right, ${baseColor}, ${shimmerColor}, ${baseColor})`,
                    }} />
                ) : (
                    <LinearGradient
                        colors={[baseColor, shimmerColor, baseColor]}
                        start={{ x: 0, y: 0.5 }}
                        end={{ x: 1, y: 0.5 }}
                        style={{width:"100%", height:"100%"}}
                    />
                )}
            </ReAnimated.View>
        </View>
    );
});

const styles = StyleSheet.create(() => ({
    container: (borderRadius, containerWidth, containerHeight, spacingVertical, baseColor) => ({
        backgroundColor: baseColor,
        borderRadius,
        width: containerWidth,
        height: containerHeight,
        overflow: 'hidden',
        marginVertical: spacingVertical,
    }),
    shimmerWrapper: {
        width: '100%',
        height: '100%',
    },
    gradient: {
        width: '100%',
        height: '100%',
    },
}));

ContentLoader.displayName = 'ContentLoader';

export default ContentLoader